/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import times from 'lodash/times';

import { Grid, Hidden, Skeleton, Typography, VisuallyHidden, breakpoints } from '@coursera/cds-core';

import ArticleHubHeroBannerPlaceholder from 'bundles/shared-articles/components/ArticleHubHeroBannerPlaceholder';
import ArticleHubCardPlaceholder from 'bundles/shared-articles/components/hub-page/ArticleHubCardPlaceholder';
import Layout from 'bundles/unified-seo-content-common/components/hub-page/Layout';
import CategoryNavigationSkeleton from 'bundles/unified-seo-content-common/components/skeletons/CategoryNavigationSkeleton';
import {
  NUMBER_OF_ITEMS_PER_LINE_DESKTOP,
  NUMBER_OF_ITEMS_PER_LINE_MOBILE,
} from 'bundles/unified-seo-content-common/constants';

import _t from 'i18n!nls/articles';

type ArticlesRowProps = {
  rowContainerOverrideStyle?: SerializedStyles;
};

const styles = {
  contentContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-800);
    ${breakpoints.down('md')} {
      gap: var(--cds-spacing-400);
    }
  `,
  fullWidthContainerContent: css`
    margin: 0 auto;
  `,
  fullWidthContainer: css`
    width: calc(100% + var(--cds-spacing-600));
    border-radius: var(--cds-spacing-100);
    background-color: var(--cds-color-neutral-background-primary-weak);
    padding: var(--cds-spacing-300);
    margin-left: calc(var(--cds-spacing-300) * -1);
  `,
  featuredContentContainer: css`
    margin-bottom: 0;
  `,
  articleRowTitle: css`
    margin-bottom: var(--cds-spacing-300);
  `,
};

const NUMBER_OF_PLACEHOLDER_ROWS = 3;

const ArticlesRowSkeleton = ({ rowContainerOverrideStyle }: ArticlesRowProps) => {
  return (
    <Grid css={rowContainerOverrideStyle} container wrap="nowrap" spacing={8}>
      {times(NUMBER_OF_ITEMS_PER_LINE_DESKTOP, (index) => (
        <Grid
          component={Hidden}
          item
          xs={6}
          md={4}
          smDown={index >= NUMBER_OF_ITEMS_PER_LINE_MOBILE}
          key={`placeholder-${index}`}
        >
          <ArticleHubCardPlaceholder />
        </Grid>
      ))}
    </Grid>
  );
};

export const HubPageSkeleton = () => {
  return (
    <div>
      <VisuallyHidden role="status">{_t('Loading Hub page data')}</VisuallyHidden>

      <Layout
        heroBannerComponent={<ArticleHubHeroBannerPlaceholder />}
        navigationComponent={<CategoryNavigationSkeleton />}
        contentComponent={
          <div css={styles.contentContainer}>
            <div css={styles.fullWidthContainer}>
              <div css={styles.fullWidthContainerContent}>
                <Typography variant="h1semibold" component="p" css={styles.articleRowTitle}>
                  <Skeleton variant="rect" width={200} />
                </Typography>
                <ArticlesRowSkeleton rowContainerOverrideStyle={styles.featuredContentContainer} />
              </div>
            </div>
            {Array.from({ length: NUMBER_OF_PLACEHOLDER_ROWS }, (v, i) => `placeholder-${i}`).map((el) => (
              <ArticlesRowSkeleton key={el} />
            ))}
          </div>
        }
      />
    </div>
  );
};

export default HubPageSkeleton;
